import type { ChangeEventHandler } from 'react'
import { useCallback, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import CharterFlightOfferAirportOptionsList from './CharterFlightOfferAirportOptionsList'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { airportLocationToAirport } from 'lib/flights/flightUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

interface Props {
  initialSelectedAirport?: App.Airport
}

function CharterFlightOfferAirportModal({ initialSelectedAirport }: Props) {
  const intl = useIntl()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useOfferPageDispatch()

  const [selectedAirport, setSelectedAirport] = useState(initialSelectedAirport)
  const [searchPhrase, setSearchPhrase] = useState('')
  const handleSearchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.target.value)
  }, [])

  const modalElement = useModalElementContext()

  const closeFilter = useCallback(() => {
    modalElement.resolve()
    trackEvent('AIRPORT_FILTER_CLOSED')
    setSearchPhrase('')
  }, [modalElement, trackEvent])

  const onAirportSelect = useCallback((airport: App.AirportLocation) => {
    setSelectedAirport(airportLocationToAirport(airport))
  }, [])

  const onConfirm = useCallback(() => {
    dispatch({
      type: OfferPageActions.setAirport,
      airport: selectedAirport,
    })
    dispatch({
      type: OfferPageActions.setFlights,
      flights: true,
    })
    trackEvent('AIRPORT_FILTER_SELECTED')
    closeFilter()
  }, [closeFilter, dispatch, selectedAirport, trackEvent])

  return (
    <Modal
      title={intl.formatMessage({
        id: 'eTDdiu1eRUhoM0N9Ve19',
        defaultMessage: 'Where will you depart from?',
      })}
      subtitle={intl.formatMessage({
        id: 'gMgqQj05WVz6hiSfl9h9',
        defaultMessage: 'Select your preferred airport',
      })}
      onClose={closeFilter}
      onPrimaryActionClick={onConfirm}
      primaryActionText={intl.formatMessage({
        id: '1YNi54fcJHFhQY249NxV',
        defaultMessage: 'Continue',
      })}
      height="full"
      tabletHeight="auto"
    >
      <VerticalSpacer gap={12}>
        <TextInputOnly
          placeholder={intl.formatMessage({
            id: 'wbkXRR_dXMkneJVtmGOL',
            defaultMessage: 'Search by city or airport',
          })}
          noValidationMessage
          noValidationSpacing
          value={searchPhrase}
          onChange={handleSearchPhraseInputChange}
          endIcon={<LineSearchIcon />}
        />
        <CharterFlightOfferAirportOptionsList
          selectedAirport={selectedAirport}
          onSelectAirport={onAirportSelect}
          withRadioButtons
          searchPhrase={searchPhrase}
        />
      </VerticalSpacer>
    </Modal>
  )
}

export default CharterFlightOfferAirportModal
